import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Heart, Shield, ArrowRight, Loader2, CheckCircle, BookOpen, Stethoscope, Users, CreditCard } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'sonner';

const DONATION_PACKAGES = [
  { id: 'kit_scolaire', label: 'Kit Scolaire', amount: 20, icon: BookOpen, desc: 'Fournir des fournitures scolaires a un enfant' },
  { id: 'microcredit', label: 'Microcredit Femme', amount: 50, icon: Users, desc: 'Financer une activite generatrice de revenus' },
  { id: 'sante', label: 'Soins de Sante', amount: 100, icon: Stethoscope, desc: 'Offrir des soins medicaux essentiels' },
  { id: 'programme', label: 'Soutien Programme', amount: 250, icon: Heart, desc: 'Financer un programme communautaire complet' },
];

const PayPalLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.103c-.543 0-1.005.392-1.09.927l-.013.09-1.06 6.722-.009.058c-.085.534-.546.926-1.09.926H7.076z" />
  </svg>
);

const Donate = () => {
  const [searchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState('microcredit');
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const paypalRef = useRef(null);
  const paypalRendered = useRef(false);

  const pollPaymentStatus = useCallback(async (sessionId, attempt) => {
    if (attempt >= 5) {
      setPaymentStatus('timeout');
      setChecking(false);
      return;
    }
    try {
      const response = await axios.get(`${API_ENDPOINTS.donationStatus}/${sessionId}`);
      const data = response.data;
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setChecking(false);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setChecking(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (err) {
      console.error('Payment status poll failed:', err);
      if (attempt >= 4) {
        setPaymentStatus('error');
        setChecking(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setChecking(true);
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams, pollPaymentStatus]);

  // Render PayPal button when PayPal method is selected
  useEffect(() => {
    if (paymentMethod === 'paypal' && paypalRef.current && !paypalRendered.current && window.PayPal?.Donation?.Button) {
      paypalRendered.current = true;
      try {
        window.PayPal.Donation.Button({
          env: 'sandbox',
          hosted_button_id: 'PAYPAL_BUTTON_ID_ICI',
          image: {
            src: 'https://www.paypalobjects.com/en_US/i/btn/btn_donateCC_LG.gif',
            title: 'PayPal - Faire un don',
            alt: 'Faire un don avec PayPal'
          },
          onComplete: function () {
            setPaymentStatus('success');
          },
        }).render('#paypal-donate-button-container');
      } catch (err) {
        console.error('PayPal button render failed:', err);
      }
    }
    if (paymentMethod !== 'paypal') {
      paypalRendered.current = false;
    }
  }, [paymentMethod]);

  const handleDonate = async () => {
    const pkg = useCustom ? null : DONATION_PACKAGES.find(p => p.id === selectedPackage);
    const amount = useCustom ? parseFloat(customAmount) : pkg?.amount;

    if (!amount || amount <= 0) {
      toast.error('Veuillez entrer un montant valide.');
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const body = useCustom
        ? { custom_amount: amount, origin_url: origin }
        : { package_id: selectedPackage, origin_url: origin };

      const response = await axios.post(API_ENDPOINTS.donationCheckout, body);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error("Donation checkout failed:", err);
      toast.error("Erreur lors de la creation du paiement. Veuillez reessayer.");
      setLoading(false);
    }
  };

  // Success State
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <Helmet><title>Merci pour votre don - HPD</title></Helmet>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-forest" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-navy mb-4">Merci pour votre generosite !</h1>
          <p className="text-gray-600 font-sans mb-8">Votre don a ete recu avec succes. Ensemble, nous construisons un avenir de paix et d'espoir.</p>
          <a href="/" data-testid="donate-back-home" className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-navy text-white font-semibold hover:bg-navy/90 transition-all font-sans">
            Retour a l'accueil
          </a>
        </motion.div>
      </div>
    );
  }

  // Error / Timeout / Expired States
  if (paymentStatus === 'timeout' || paymentStatus === 'error' || paymentStatus === 'expired') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <Helmet><title>Statut du paiement - HPD</title></Helmet>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-navy mb-4">
            {paymentStatus === 'expired' ? 'Session expiree' : 'Verification en attente'}
          </h1>
          <p className="text-gray-600 font-sans mb-8">
            {paymentStatus === 'expired'
              ? 'Votre session de paiement a expire. Si vous avez ete debite, votre don sera bien pris en compte.'
              : 'Nous n\'avons pas encore pu confirmer votre paiement. Si vous avez complete le paiement, il sera traite automatiquement.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => { setPaymentStatus(null); setChecking(false); }}
              data-testid="donate-retry-btn"
              className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-forest text-white font-semibold hover:bg-forest/90 transition-all font-sans"
            >
              Faire un nouveau don
            </button>
            <a href="/" className="inline-flex items-center justify-center px-8 py-3 rounded-full border-2 border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-all font-sans">
              Retour a l'accueil
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // Checking State
  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <Helmet><title>Verification du paiement - HPD</title></Helmet>
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-navy animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-sans">Verification du paiement en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Faire un Don - Hope for Peace and Development</title>
        <meta name="description" content="Soutenez HPD par un don pour l'autonomisation des femmes et la paix en RDC." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">Soutenez Notre Mission</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">Faire un Don</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-2xl font-sans">
              Chaque contribution aide a transformer des vies. Votre generosite soutient directement nos programmes de paix et d'autonomisation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left: Impact info */}
            <div className="lg:col-span-2">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-serif font-bold text-navy mb-6">Votre Impact</h2>
                <div className="space-y-4">
                  {DONATION_PACKAGES.map((pkg) => {
                    const Icon = pkg.icon;
                    return (
                      <div key={pkg.id} className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-black/5">
                        <div className="w-10 h-10 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-forest" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy font-sans text-sm">${pkg.amount} - {pkg.label}</p>
                          <p className="text-gray-500 text-xs font-sans">{pkg.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 p-4 bg-navy/5 rounded-2xl flex items-center gap-3">
                  <Shield className="w-5 h-5 text-navy flex-shrink-0" />
                  <p className="text-xs text-gray-600 font-sans">Paiements securises. Vos informations sont protegees.</p>
                </div>
              </motion.div>
            </div>

            {/* Right: Checkout form */}
            <div className="lg:col-span-3">
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-8 border border-black/5 shadow-sm">
                <h3 className="text-xl font-serif font-bold text-navy mb-6">Choisissez votre don</h3>

                {/* Package Selection */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {DONATION_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => { setSelectedPackage(pkg.id); setUseCustom(false); }}
                      data-testid={`donate-pkg-${pkg.id}`}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                        !useCustom && selectedPackage === pkg.id
                          ? 'border-forest bg-forest/5'
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <p className="text-2xl font-serif font-bold text-navy">${pkg.amount}</p>
                      <p className="text-xs text-gray-500 font-sans mt-1">{pkg.label}</p>
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mb-6">
                  <button
                    onClick={() => setUseCustom(true)}
                    data-testid="donate-custom-toggle"
                    className={`text-sm font-sans font-medium mb-3 ${useCustom ? 'text-forest' : 'text-gray-500 hover:text-navy'} transition-colors`}
                  >
                    Montant personnalise
                  </button>
                  {useCustom && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-2">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          placeholder="Montant"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          data-testid="donate-custom-amount"
                          className="w-full pl-8 pr-4 py-4 rounded-2xl border border-gray-200 text-2xl font-serif font-bold text-navy focus:outline-none focus:border-forest"
                        />
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Payment Method Selector */}
                <div className="mb-6">
                  <p className="text-sm font-sans font-medium text-gray-700 mb-3">Methode de paiement</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      data-testid="payment-method-stripe"
                      className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 ${
                        paymentMethod === 'stripe' ? 'border-forest bg-forest/5' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <CreditCard className="w-5 h-5 text-navy" />
                      <span className="font-sans font-semibold text-sm text-navy">Carte bancaire</span>
                    </button>
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      data-testid="payment-method-paypal"
                      className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 ${
                        paymentMethod === 'paypal' ? 'border-[#0070ba] bg-[#0070ba]/5' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <PayPalLogo />
                      <span className="font-sans font-semibold text-sm text-navy">PayPal</span>
                    </button>
                  </div>
                </div>

                {/* Stripe Donate Button */}
                {paymentMethod === 'stripe' && (
                  <>
                    <button
                      onClick={handleDonate}
                      disabled={loading}
                      data-testid="donate-submit-btn"
                      className="w-full inline-flex items-center justify-center px-8 py-4 rounded-full bg-forest text-white font-semibold text-lg hover:bg-forest/90 disabled:opacity-50 transition-all duration-300 shadow-lg group font-sans"
                    >
                      {loading ? (
                        <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Redirection vers le paiement...</>
                      ) : (
                        <>
                          <Heart className="mr-2 w-5 h-5" fill="currentColor" />
                          Faire un don de ${useCustom ? (customAmount || '0') : DONATION_PACKAGES.find(p => p.id === selectedPackage)?.amount}
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-4 font-sans">
                      Vous serez redirige vers la page de paiement securisee Stripe
                    </p>
                  </>
                )}

                {/* PayPal Donate Button */}
                {paymentMethod === 'paypal' && (
                  <div className="text-center">
                    <div id="paypal-donate-button-container" ref={paypalRef} className="flex justify-center mb-4" data-testid="paypal-donate-container" />
                    <p className="text-xs text-gray-400 font-sans mt-2">
                      Vous serez redirige vers PayPal pour completer votre don
                    </p>
                    <div className="mt-4 p-4 bg-blue-50 rounded-2xl">
                      <p className="text-xs text-blue-700 font-sans">
                        <strong>Note :</strong> Le bouton PayPal sera actif une fois que HPD aura configure son compte PayPal Business.
                        En attendant, vous pouvez utiliser la carte bancaire via Stripe.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
